import { getSessionId } from '../lib/supabase';
import { isServer, getApiBaseUrl, logger } from '../lib/utils';

// Use the utility function to get the API base URL
const API_BASE_URL = getApiBaseUrl();

// Define constants for verification URLs
const TEST_VERIFICATION_URL = 'https://verify.stripe.com/v/test_9AQcNA6Pm75v6t23cc';
const PROD_VERIFICATION_URL = 'https://verify.stripe.com/v/dR6dTGbtJ7lEdBC288';

export interface VerificationRecord {
  id?: number;
  lead_id: number;
  session_id: string;
  client_reference_id: string;
  email: string;
  verification_url: string;
  status: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Create a Stripe checkout session
 */
export async function createCheckoutSession(
  priceId: string,
  successUrl: string,
  cancelUrl: string,
  clientReferenceId?: string,
  customerEmail?: string
): Promise<{ sessionId: string; url: string } | null> {
  try {
    if (isServer()) {
      logger.warn('createCheckoutSession should not be called server-side');
      return null;
    }

    const response = await fetch(`${API_BASE_URL}/stripe/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        priceId,
        successUrl,
        cancelUrl,
        clientReferenceId,
        customerEmail
      }),
    });

    if (!response.ok) {
      logger.error('Failed to create checkout session:', await response.text());
      return null;
    }

    return await response.json();
  } catch (error) {
    logger.error('Error creating checkout session:', error);
    return null;
  }
}

/**
 * Create an identity verification URL with query parameters
 * @param clientReferenceId - The client reference ID to associate with the verification
 * @param email - The email to prefill in the verification form
 * @returns The verification URL with query parameters
 */
export const createIdentityVerificationUrl = (clientReferenceId: string, email: string): string => {
  // Check if URL contains stripetestverification=true parameter
  const urlParams = new URLSearchParams(window.location.search);
  const urlHasParam = urlParams.get('stripetestverification');
  
  // Also check localStorage for stored query string
  const storedQueryString = localStorage.getItem('adressfinder_url_query_string') || '';
  const storedParams = new URLSearchParams(storedQueryString);
  const storedHasParam = storedParams.get('stripetestverification');
  
  // Use test URL if either current URL or stored URL has stripetestverification=true
  const useTestVerification = urlHasParam === 'true' || storedHasParam === 'true';
  
  // Debug logs
  console.log('DEBUG: Verification URL params check', {
    urlParamValue: urlHasParam,
    storedParamValue: storedHasParam,
    fullUrl: window.location.href,
    storedQueryString,
    useTestVerification
  });
  
  // Select the appropriate base URL
  const baseUrl = useTestVerification 
    ? TEST_VERIFICATION_URL
    : PROD_VERIFICATION_URL;

  logger.log('Creating identity verification URL with parameters:', {
    clientReferenceId,
    email,
    baseUrl,
    isTestMode: useTestVerification
  });
  
  try {
    // Add query parameters
    const url = new URL(baseUrl);
    url.searchParams.append('client_reference_id', clientReferenceId);
    url.searchParams.append('prefilled_email', email);
    
    // Add success_url parameter for redirect after verification
    // Get the current domain
    const domain = window.location.origin;
    const successUrl = `${domain}/verification-success?session_id={VERIFICATION_SESSION_ID}`;
    url.searchParams.append('success_url', encodeURIComponent(successUrl));
    
    // Add the formatted URL to the audit log
    logger.log('Formatted verification URL created');
    
    return url.toString();
  } catch (error) {
    // Handle URL construction error
    logger.error('Error creating verification URL:', error);
    
    // Basic fallback URL construction if URL constructor fails
    return `${baseUrl}?client_reference_id=${encodeURIComponent(clientReferenceId)}&prefilled_email=${encodeURIComponent(email)}`;
  }
};

/**
 * Create a Stripe Identity verification URL
 */
export async function createVerificationUrl(email: string, leadId: number): Promise<{ url: string; clientReferenceId: string; sessionId: string } | null> {
  try {
    if (isServer()) {
      logger.warn('createVerificationUrl should not be called server-side');
      return null;
    }

    logger.log('Creating verification URL for:', {
      email,
      leadId
    });

    // Get the current auftragsnummer from localStorage
    const auftragsnummer = typeof window !== 'undefined' ? localStorage.getItem('auftragsnummer') || 'unknown' : 'unknown';
    logger.log('Retrieved auftragsnummer from localStorage:', auftragsnummer);
    
    // Get the current session ID
    const sessionId = await getSessionId() || `vs_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    logger.log('Using session ID for verification:', sessionId);
    
    // Create the same client reference ID format as used in the payment link
    const clientReferenceId = `${auftragsnummer}_${sessionId}`;
    logger.log('Generated client reference ID:', clientReferenceId);
    
    // Create the verification URL with query parameters
    const url = createIdentityVerificationUrl(clientReferenceId, email);
    
    logger.log('Created verification URL with parameters:', {
      url,
      urlLength: url.length,
      clientReferenceId,
      sessionId,
      email
    });
    
    return {
      url,
      clientReferenceId,
      sessionId,
    };
  } catch (error) {
    logger.error('Error creating verification URL:', error);
    logger.error('Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      email,
      leadId
    });
    return null;
  }
}

/**
 * Record a verification session in the database
 */
export async function recordVerificationSession(
  leadId: number,
  sessionId: string,
  clientReferenceId: string,
  email: string,
  verificationUrl: string
): Promise<VerificationRecord | null> {
  try {
    if (isServer()) {
      logger.warn('recordVerificationSession should not be called server-side');
      return null;
    }

    // Check if we're in test mode
    const urlParams = new URLSearchParams(window.location.search);
    const storedQueryString = localStorage.getItem('adressfinder_url_query_string') || '';
    const storedParams = new URLSearchParams(storedQueryString);
    const isTestMode = urlParams.get('stripetestverification') === 'true' || storedParams.get('stripetestverification') === 'true';

    logger.log('Recording verification session with data:', {
      leadId,
      sessionId,
      clientReferenceId,
      email,
      verificationUrlLength: verificationUrl ? verificationUrl.length : 0
    });

    // Validate required fields
    if (!leadId || !sessionId || !clientReferenceId || !email || !verificationUrl) {
      logger.error('Missing required fields for verification session:', {
        hasLeadId: !!leadId,
        hasSessionId: !!sessionId,
        hasClientReferenceId: !!clientReferenceId,
        hasEmail: !!email,
        hasVerificationUrl: !!verificationUrl
      });
      return null;
    }

    const response = await fetch(`${API_BASE_URL}/verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Id': sessionStorage.getItem('funnel_session_id') || ''
      },
      body: JSON.stringify({
        leadId,
        sessionId,
        clientReferenceId,
        email,
        verificationUrl,
        stripetestverification: isTestMode ? 'true' : 'false'
      }),
    });

    logger.log('Verification session record API response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('Failed to record verification session:', errorText);
      logger.error('Response details:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries([...response.headers.entries()]),
        url: response.url
      });
      return null;
    }

    const responseData = await response.json();
    logger.log('Verification session recorded successfully:', responseData);
    return responseData;
  } catch (error) {
    logger.error('Error recording verification session:', error);
    logger.error('Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack,
      leadId,
      sessionId,
      clientReferenceId
    });
    return null;
  }
}

/**
 * Gets a verification session by client reference ID
 * @param clientReferenceId - The client reference ID
 * @returns The verification record or null if not found
 */
export async function getVerificationByClientReferenceId(
  clientReferenceId: string
): Promise<VerificationRecord | null> {
  try {
    if (isServer()) {
      logger.warn('getVerificationByClientReferenceId should not be called server-side');
      return null;
    }

    const response = await fetch(`${API_BASE_URL}/verification?clientReferenceId=${encodeURIComponent(clientReferenceId)}`, {
      method: 'GET',
    });

    // Parse the response data
    const responseData = await response.json();
    
    // Check if the response indicates no verification was found
    if (!response.ok || responseData.success === false || !responseData.data) {
      logger.log('No verification found for client reference ID:', clientReferenceId);
      return null;
    }

    return responseData.data || responseData;
  } catch (error) {
    logger.error('Error getting verification by client reference ID:', error);
    return null;
  }
}

/**
 * Update verification status
 */
export async function updateVerificationStatus(id: number, status: string): Promise<boolean> {
  try {
    if (isServer()) {
      logger.warn('updateVerificationStatus should not be called server-side');
      return false;
    }

    const response = await fetch(`${API_BASE_URL}/verification?id=${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        status,
      }),
    });

    if (!response.ok) {
      logger.error('Failed to update verification status:', await response.text());
      return false;
    }

    return true;
  } catch (error) {
    logger.error('Error updating verification status:', error);
    return false;
  }
}

/**
 * Gets a verification session by Stripe verification session ID via Edge Function
 * @param stripeVerificationSessionId - The Stripe verification session ID
 * @returns The verification record or null if not found
 */
export const getVerificationByStripeSessionId = async (
  stripeVerificationSessionId: string
): Promise<VerificationRecord | null> => {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const response = await fetch(`${supabaseUrl}/functions/v1/get-verification-by-stripe-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        stripe_verification_session_id: stripeVerificationSessionId
      })
    });

    const responseData = await response.json();

    if (!response.ok || !responseData.success) {
      logger.error('Error getting verification by Stripe session ID:', responseData.error);
      return null;
    }

    return responseData.data;
  } catch (error) {
    logger.error('Exception getting verification by Stripe session ID:', error);
    return null;
  }
}; 